/* Pages */

import { useEffect } from "react";

import { Redirect, Route, Switch } from "react-router-dom";

import { AppSummaryPage } from "manager/components/app-summary";
import Layout from "manager/components/Layout";
import { APPLICANT, APPLICATION, PORTFOLIO, UNIT } from "manager/config/mocks";
import ROUTES from "manager/config/routes";
import { getBackLinks, getHeader, getSidebar } from "manager/hooks/use-layout";
import { useManagerTranslate } from "manager/hooks/use-manager-translate";
import Applicant from "manager/pages/Applicant";
import Application from "manager/pages/Application";
import ApplicationSetup from "manager/pages/ApplicationSetup";
import Dashboard from "manager/pages/Dashboard";
import DealExtraDetails from "manager/pages/DealExtraDetails";
import Deals from "manager/pages/Deals";
import DownloadCenter from "manager/pages/DownloadCenter";
import EmailBuilder from "manager/pages/EmailBuilder";
import Employee from "manager/pages/Employee";
import Employees from "manager/pages/Employees";
import ExperianAuth from "manager/pages/ExperianAuth";
import { BACKGROUND_CREDIT_CHECK_PROVIDER } from "manager/pages/ExperianAuth/interfaces";
import { isExperianProvider } from "manager/pages/ExperianAuth/utils";
import { LumenRoutes } from "manager/pages/Lumen";
import MagicLinks from "manager/pages/MagicLinks";
import { useGetMarketplaceRoutes } from "manager/pages/Marketplace/use-marketplace-routes";
import OpenApiSettings from "manager/pages/OpenApiSettings";
import PasswordRecoverySetPassword from "manager/pages/PasswordRecoverySetPassword";
import Portfolio from "manager/pages/Portfolio";
import PortfolioSettings from "manager/pages/PortfolioSettings";
import {
  CompanyPrescreeningSettings,
  LeadDetails,
  Leads,
} from "manager/pages/Prescreening";
import Properties from "manager/pages/Properties";
import PropertySettings from "manager/pages/PropertySettings";
import SelectCompany from "manager/pages/SelectCompany";
import SetPassword from "manager/pages/SetPassword";
import Settings from "manager/pages/Settings";
import Unit from "manager/pages/Unit";

import App from "shared/components/App";
import { RenderModal } from "shared/components/Modal";
import { openClickwrapModal } from "shared/components/Modals/ClickwrapModal";
import {
  APPS,
  EMPLOYEE_GROUP,
  ICRAA_GROUP_MODAL_CONTENT,
} from "shared/config/constants";
import {
  useActiveUser,
  checkPactSafeAgreement,
  useGetPactSafeConfig,
} from "shared/hooks/api";
import { usePermissions } from "shared/hooks";
import Error404Page from "shared/pages/Error404Page";
import PasswordRecoveryEmailSentPage from "shared/pages/PasswordRecoveryEmailSentPage";
import PasswordRecoveryPage from "shared/pages/PasswordRecoveryPage";
import SetPasswordLinkAcceptedPage from "shared/pages/SetPasswordLinkAcceptedPage";
import SetPasswordLinkExpiredPage from "shared/pages/SetPasswordLinkExpiredPage";
import SetPasswordLinkSentPage from "shared/pages/SetPasswordLinkSentPage";
import { useClaims } from "shared/utils/auth";
import { ConditionalPage, ConditionalRoute } from "shared/utils/routing";
import { useVeroFlags } from "shared/utils/launchDarkly";

/* Utils */

type ManagerAppProps = {
  company?: {
    name?: string;
    shortName?: string;
    logo?: string;
    slug: string;
    bccProvider?: BACKGROUND_CREDIT_CHECK_PROVIDER;
  };
  userExperianAuthenticated?: boolean;
  hasPermission: (permission: any) => boolean;
  permissionsLoaded: boolean;
  isMagicLinksEnabled?: boolean;
  isOpenApiEnabled: boolean;
  isEmailCustomizationEnabled: boolean;
  isPrescreeningEnabled?: boolean;
};

const EXPERIAN_BYPASS_KEY = "experian_auth_bypassed";

const experianPrompt = (
  company,
  userExperianAuthenticated,
  canBypassExperianAuth
) => {
  // Only check if user has bypassed if the user has permission to bypass
  const hasUserBypassed = canBypassExperianAuth
    ? sessionStorage.getItem(EXPERIAN_BYPASS_KEY) === "true"
    : false;

  return (
    isExperianProvider(company) &&
    !userExperianAuthenticated &&
    (!canBypassExperianAuth || // If no permission to bypass, always prompt
      (canBypassExperianAuth && !hasUserBypassed)) // If has permission, only prompt if not bypassed
  );
};

const ExperianProtected = (props) => {
  const { company, userExperianAuthenticated, permissionsLoaded, path } = props;
  const { canBypassExperianAuth } = usePermissions();

  if (
    company === undefined ||
    !permissionsLoaded ||
    (company &&
      !experianPrompt(
        company,
        userExperianAuthenticated,
        canBypassExperianAuth
      ))
  ) {
    return <Route {...props} />;
  }

  return (
    <Redirect
      from={path}
      to={{
        pathname: ROUTES.dashboard,
        state: { experianPrompt: true },
      }}
    />
  );
};

const ManagerApp = ({
  company,
  userExperianAuthenticated = false,
  hasPermission,
  permissionsLoaded,
  isMagicLinksEnabled = false,
  isOpenApiEnabled,
  isEmailCustomizationEnabled,
  isPrescreeningEnabled = false,
}: ManagerAppProps) => {
  const { t } = useManagerTranslate();
  const { activeUser } = useActiveUser();
  const { claims } = useClaims();
  const { canBypassExperianAuth } = usePermissions();
  const { useShowIcraaClickWrapModal } = useVeroFlags();

  const { getMarketplaceRoutes } = useGetMarketplaceRoutes();
  const { pactSafeConfig } = useGetPactSafeConfig();

  useEffect(() => {
    async function handleAgreementCheckAndModal() {
      /**
       * Make sure
       * email laoded & cognitoGroups loaded and has EMPLOYEE_GROUP
       * pactSafeConfig loaded and has accessId
       * company?.bccProvider is TU_SHAREABLE
       * above 3 true? Check if user has accepted the agreement
       * if not, open the modal
       * if yes, do nothing
       */
      if (!useShowIcraaClickWrapModal) {
        return;
      }
      if (
        activeUser?.email &&
        claims?.cognitoGroups?.includes(EMPLOYEE_GROUP) &&
        pactSafeConfig?.accessId &&
        company?.bccProvider === BACKGROUND_CREDIT_CHECK_PROVIDER.TU_SHAREABLE
      ) {
        try {
          const hasAccepted = await checkPactSafeAgreement({
            signerEmail: activeUser?.email,
            groupKey: pactSafeConfig?.termsOfServiceIcraaGroupKey,
            accessId: pactSafeConfig?.accessId,
          });
          if (!hasAccepted) {
            const modalApi = openClickwrapModal({
              onConfirm: () => {
                if (modalApi) modalApi.closeDialog();
              },
              groupKey: pactSafeConfig?.termsOfServiceIcraaGroupKey,
              userEmail: activeUser?.email,
              customData: {},
              title: ICRAA_GROUP_MODAL_CONTENT.title,
              description: ICRAA_GROUP_MODAL_CONTENT.description,
              closable: false,
              showCancelLink: false,
            });
          }
        } catch (error) {
          console.error(error);
        }
      }
    }
    handleAgreementCheckAndModal();
  }, [
    activeUser?.email,
    claims?.cognitoGroups,
    pactSafeConfig?.accessId,
    company?.bccProvider,
    useShowIcraaClickWrapModal,
  ]);

  return (
    <App
      id={APPS.manager}
      render={({ history, user }) => {
        const sidebar = getSidebar(
          t,
          // @ts-ignore
          company,
          isPrescreeningEnabled
        );
        const header = getHeader(user, isMagicLinksEnabled);
        const backLinks = getBackLinks(t);
        const layoutProps = {
          user,
          company,
          isMagicLinksEnabled,
          isPrescreeningEnabled,
        };

        return (
          <>
            <Switch>
              <Route
                exact
                path={ROUTES.dashboard}
                render={() => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.dashboard,
                    }}
                  >
                    <Dashboard backLink={backLinks.dashboard} />
                  </Layout>
                )}
              />
              {/* Table views */}
              <Route
                exact
                path={ROUTES.leads}
                render={(params) => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                    }}
                  >
                    <ConditionalPage
                      params={params}
                      isInitialized={permissionsLoaded}
                      isEnabled={isPrescreeningEnabled}
                    >
                      <Leads />
                    </ConditionalPage>
                  </Layout>
                )}
              />
              <Route
                exact
                path={ROUTES.deals}
                render={() => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                    }}
                  >
                    <Deals
                      experianPrompt={experianPrompt(
                        company,
                        userExperianAuthenticated,
                        canBypassExperianAuth
                      )}
                    />
                  </Layout>
                )}
              />
              <ExperianProtected
                permissionsLoaded={permissionsLoaded}
                company={company}
                userExperianAuthenticated={userExperianAuthenticated}
                path={ROUTES.deal}
                // @ts-ignore
                render={() => <AppSummaryPage {...layoutProps} />}
              />
              <Route
                exact
                path={ROUTES.employees}
                render={() => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.employees,
                    }}
                  >
                    <Employees backLink={backLinks.employees} />
                  </Layout>
                )}
              />
              {[ROUTES.employees, ROUTES.permissions].map((route) => (
                <Route
                  key={route}
                  exact
                  path={route}
                  render={() => (
                    <Layout
                      sidebar={sidebar}
                      header={{
                        ...header,
                        backLink: backLinks.employees,
                      }}
                    >
                      <Employees backLink={backLinks.employees} />
                    </Layout>
                  )}
                />
              ))}
              {[ROUTES.units, ROUTES.properties, ROUTES.portfolios].map(
                (route) => (
                  <Route
                    key={route}
                    exact
                    path={route}
                    render={() => (
                      <Layout sidebar={sidebar} header={header}>
                        <Properties />
                      </Layout>
                    )}
                  />
                )
              )}
              <Route
                path={ROUTES.applicationsSetup}
                render={() => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.applicationsSetup,
                    }}
                  >
                    <ApplicationSetup backLink={backLinks.applicationsSetup} />
                  </Layout>
                )}
              />
              <Route
                path={ROUTES.prescreeningSetup}
                render={() => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.prescreeningSetup,
                    }}
                  >
                    <CompanyPrescreeningSettings
                      backLink={backLinks.prescreeningSetup}
                    />
                  </Layout>
                )}
              />
              {/* Other views */}
              <Route
                path={ROUTES.lead}
                render={(params) => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.lead,
                    }}
                    withoutBackToTop
                  >
                    <ConditionalPage
                      params={params}
                      isInitialized={permissionsLoaded}
                      isEnabled={isPrescreeningEnabled}
                    >
                      <LeadDetails backLink={backLinks.lead} />
                    </ConditionalPage>
                  </Layout>
                )}
              />
              <Route
                exact
                path={ROUTES.dealExtraDetails}
                render={({
                  match: {
                    params: { id },
                  },
                }) => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.dealExtraDetails(id),
                    }}
                    withoutBackToTop
                  >
                    <DealExtraDetails
                      backLink={backLinks.dealExtraDetails(id)}
                    />
                  </Layout>
                )}
              />
              <ExperianProtected
                permissionsLoaded={permissionsLoaded}
                company={company}
                userExperianAuthenticated={userExperianAuthenticated}
                path={ROUTES.application}
                render={({ match }) => (
                  <Application
                    sidebar={sidebar}
                    header={header}
                    {...APPLICATION}
                    backLink={backLinks.application}
                    match={match}
                  />
                )}
              />
              <Route
                path={ROUTES.property}
                render={({
                  match: {
                    params: { id },
                  },
                }) => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.propertySettings(id),
                    }}
                  >
                    <PropertySettings
                      backLink={backLinks.propertySettings(id)}
                      // @ts-ignore
                      sidebar={sidebar}
                      header={header}
                    />
                  </Layout>
                )}
              />
              <Route
                exact
                path={ROUTES.portfolio}
                render={() => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.portfolio,
                    }}
                  >
                    <Portfolio {...PORTFOLIO} backLink={backLinks.portfolio} />
                  </Layout>
                )}
              />
              <Route
                path={ROUTES.portfolioSettings}
                render={() => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.portfolio,
                    }}
                  >
                    <PortfolioSettings backLink={backLinks.portfolio} />
                  </Layout>
                )}
              />
              <Redirect exact from={ROUTES.unit} to={ROUTES.unitListings} />
              <Route
                path={[
                  ROUTES.unitListings,
                  ROUTES.unitDeals,
                  ROUTES.unitFees,
                  ROUTES.unitWorkflow,
                  ROUTES.unitLumen,
                ]}
                render={(routeProps) => (
                  <Unit
                    {...UNIT}
                    //  @ts-ignore
                    sidebar={sidebar}
                    //  @ts-ignore
                    header={header}
                    id={routeProps?.match?.params?.id}
                    company={company}
                  />
                )}
              />
              <Route
                exact
                path={ROUTES.employee}
                render={() => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.employee,
                    }}
                  >
                    <Employee backLink={backLinks.employee} />
                  </Layout>
                )}
              />
              <Route
                exact
                path={ROUTES.applicant}
                render={() => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.applicant,
                    }}
                  >
                    <Applicant
                      {...APPLICANT}
                      history={history}
                      backLink={backLinks.applicant}
                    />
                  </Layout>
                )}
              />
              {getMarketplaceRoutes(
                sidebar,
                header,
                backLinks,
                permissionsLoaded,
                hasPermission,
                company
              )}
              <Route
                path={ROUTES.settings}
                render={() => (
                  <Layout sidebar={sidebar} header={header}>
                    <Settings />
                  </Layout>
                )}
              />
              <Route
                path={ROUTES.magicLinks}
                render={() => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.magicLinks,
                    }}
                  >
                    <MagicLinks backLink={backLinks.magicLinks} />
                  </Layout>
                )}
              />
              <Route
                path={ROUTES.downloadCenter}
                render={() => (
                  <Layout
                    sidebar={sidebar}
                    header={{
                      ...header,
                      backLink: backLinks.downloadCenter,
                    }}
                  >
                    <DownloadCenter backLink={backLinks.downloadCenter} />
                  </Layout>
                )}
              />
              <ConditionalRoute
                path={ROUTES.openApiSettings}
                redirectTo={ROUTES.notFound}
                isEnabled={isOpenApiEnabled}
                isInitialized={permissionsLoaded}
              >
                <Layout
                  sidebar={sidebar}
                  header={{
                    ...header,
                    backLink: backLinks.openApiSettings,
                  }}
                >
                  <OpenApiSettings backLink={backLinks.openApiSettings} />
                </Layout>
              </ConditionalRoute>

              <Route
                path={ROUTES.lumen}
                render={() => (
                  <LumenRoutes
                    // @ts-ignore
                    header={header}
                    // @ts-ignore
                    backLinks={backLinks}
                    // @ts-ignore
                    sidebar={sidebar}
                  />
                )}
              />

              <ConditionalRoute
                exact
                path={[ROUTES.emailBuilder, ROUTES.emailBuilderEdit]}
                isEnabled={isEmailCustomizationEnabled}
                isInitialized={permissionsLoaded}
                redirectTo={ROUTES.notFound}
              >
                <EmailBuilder header={header} sidebar={sidebar} />
              </ConditionalRoute>

              {/* Auth views */}
              <Route
                exact
                path={ROUTES.setPassword}
                render={() => <SetPassword />}
              />
              <Route
                exact
                path={ROUTES.setPasswordLinkExpired}
                render={({ match }) => (
                  <SetPasswordLinkExpiredPage token={match.params.token} />
                )}
              />
              <Route
                exact
                path={ROUTES.setPasswordLinkSent}
                render={() => <SetPasswordLinkSentPage />}
              />
              <Route
                exact
                path={ROUTES.setPasswordLinkAccepted}
                render={() => <SetPasswordLinkAcceptedPage />}
              />
              <Route
                exact
                path={ROUTES.passwordRecovery}
                render={() => <PasswordRecoveryPage />}
              />
              <Route
                exact
                path={ROUTES.passwordRecoveryEmailSent}
                render={({ location }) => (
                  // @ts-ignore
                  <PasswordRecoveryEmailSentPage email={location.state.email} />
                )}
              />
              <Route
                exact
                path={ROUTES.passwordRecoverySetPassword}
                render={() => <PasswordRecoverySetPassword />}
              />
              <Route
                exact
                path={ROUTES.selectCompany}
                render={() => <SelectCompany />}
              />
              <Route
                exact
                path={ROUTES.experianAuthentication}
                render={() => <ExperianAuth company={company} />}
              />
              {/* Other views */}
              <Route render={() => <Error404Page />} />
            </Switch>
            {/* Modals */}
            <RenderModal />
          </>
        );
      }}
    />
  );
};

export default ManagerApp;
